import { JSX } from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";
import { DEFAULT_FONT_COLOR, setSettings } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { WorkExperience } from "./WorkExperience";
import { Education } from "./Education";
import { Project } from "./Project";
import { Skills } from "./Skills";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { Summary } from "./Summary";
import { Profile } from "./Profile";
import { A4_HEIGHT_PT, LETTER_HEIGHT_PT } from "lib/constants";
import { ResumePDFLink, ResumePDFText } from "../ResumePDF/common";
import { IconType, ResumePDFIcon } from "../ResumePDF/common/ResumePDFIcon";
import { extractUsernameFromURL } from "lib/utils/urlHelpers";

export const TemplateFourteen = ({
  resume,
  settings,
  isPDF = false,
  enableHighlight = false,
  enableMistakeHighlight = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
  } = settings;

  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;
  const { email, phone, url1, url2, summary, location } = profile;
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);
  const leftRightFormOrder = settings.leftRightFormOrder;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);
  const leftShowFormsOrder = leftRightFormOrder?.left?.filter(
    (form) => formToShow[form]
  );
  const rightShowFormsOrder = leftRightFormOrder?.right?.filter(
    (form) => formToShow[form]
  );

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <WorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    skills: () => (
      <Skills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    languages: () => (
      <Language
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        isPDF={isPDF}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    awards: () => (
      <Award
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        isPDF={isPDF}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    custom: () => (
      <Custom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
  };
  const iconProps = { email, phone, location, url1, url2 };
  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
            height:
              documentSize === "A4"
                ? `${A4_HEIGHT_PT}pt`
                : `${LETTER_HEIGHT_PT}pt`,
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View style={{ ...styles.flexRow, height: `${spacing[5]}` }} fixed>
              <View
                style={{
                  width: "30%",
                  backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
                  paddingLeft: spacing[8],
                  paddingRight: spacing[8],
                }}
            ></View>
            <View
              style={{
                width: "70%",
                color: "black",
                // backgroundColor: "white",
                paddingLeft: spacing[8],
                paddingRight: spacing[8],
              }}
            ></View>
          </View>
          <View
            style={{
              ...styles.flexRow,
              marginTop: `-${spacing[0.5]}`,
            }}
          >
            <View
              style={{
                backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
                width: "30%",
                padding: `${spacing[10]} ${spacing[8]} ${spacing[0]} ${spacing[8]}`,
              }}
            >
              <View
                style={{
                  ...styles.flexCol,
                  gap: spacing["3"],
                  alignItems: "flex-start",
                  // marginTop: spacing["4"],
                }}
              >
                <Profile
                  profile={profile}
                  themeColor={themeColor}
                  isPDF={isPDF}
                  workExperiences={workExperiences}
                  enableHighlight={enableHighlight}
                />

                {Object.entries(iconProps).map(([key, value]) => {
                  if (!value) return null;

                  let iconType = key as IconType;
                  let displayValue = value;
                  if (key === "url1" || key === "url2") {
                    if (value.includes("github")) {
                      iconType = "url_github";
                    } else if (value.includes("linkedin")) {
                      iconType = "url_linkedin";
                    } else if (value.includes("behance")) {
                      iconType = "url_behance";
                    } else if (value.includes("dribble")) {
                      iconType = "url_dribble";
                    }
                    displayValue = extractUsernameFromURL(value);
                  }

                  const shouldUseLinkWrapper = [
                    "email",
                    "url1",
                    "url2",
                    "phone",
                  ].includes(key);
                  const Wrapper = ({
                    children,
                  }: {
                    children: React.ReactNode;
                  }) => {
                    if (!shouldUseLinkWrapper) return <>{children}</>;

                    let src = "";
                    switch (key) {
                      case "email": {
                        src = `mailto:${value}`;
                        break;
                      }
                      case "phone": {
                        src = `tel:${value.replace(/[^\d+]/g, "")}`; // Keep only + and digits
                        break;
                      }
                      default: {
                        src = value.startsWith("http")
                          ? value
                          : `https://${value}`;
                      }
                    }

                    return (
                      <ResumePDFLink src={src} isPDF={isPDF}>
                        {children}
                      </ResumePDFLink>
                    );
                  };

                  return (
                    <View
                      key={key}
                      style={{
                        ...styles.flexRow,
                        alignItems: "center",
                        gap: spacing["1"],
                      }}
                    >
                      <ResumePDFIcon
                        type={iconType}
                        isPDF={isPDF}
                        themeColor="black"
                      />
                      <Wrapper>
                        <ResumePDFText themeColor="black">
                          {displayValue}
                        </ResumePDFText>
                      </Wrapper>
                    </View>
                  );
                })}
              </View>

              {profile.summary && (
                <Summary
                  heading="PROFILE"
                  summary={profile.summary}
                  themeColor={themeColor}
                  enableHighlight={enableHighlight}
                  enableMistakeHighlight={enableMistakeHighlight}
                />
              )}
              {leftShowFormsOrder?.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View
              style={{
                width: "70%",
                // backgroundColor: "white",
                padding: `${spacing[10]} ${spacing[8]} ${spacing[0]} ${spacing[8]}`,
              }}
            >
              {rightShowFormsOrder?.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
          </View>
          <View
            style={{
              flex: 1,
              width: "100%",
              ...styles.flexRow,
              marginTop: `-${spacing[0.5]}`,
            }}
          >
            <View
              style={{
                width: "30%",
                backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
                paddingLeft: spacing[8],
                paddingRight: spacing[8],
              }}
            ></View>
            <View
              style={{
                width: "70%",
                // backgroundColor: "white",
                paddingLeft: spacing[8],
                paddingRight: spacing[8],
              }}
            ></View>
          </View>
          <View
            style={{
              ...styles.flexRow,
              height: `${spacing[5]}`,
              marginTop: `-${spacing[0.5]}`,
            }}
            fixed
          >
            <View
              style={{
                width: "30%",
                backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
                paddingLeft: spacing[8],
                paddingRight: spacing[8],
              }}
            ></View>
            <View
              style={{
                width: "70%",
                // backgroundColor: "white",
                paddingLeft: spacing[8],
                paddingRight: spacing[8],
              }}
            ></View>
          </View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
